import { useState, useEffect, useCallback, useMemo } from 'react';
import { Music, Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_9013cffc-5815-4696-af30-6ef829e3ad5d/artifacts/390zcaea_logo.png";

function ArtistPlaceholder({ name }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2);
  return (
    <div className="w-full h-full bg-gradient-to-b from-[#122036] to-[#0d1b2e] flex flex-col items-center justify-center gap-3">
      <img src={LOGO_URL} alt="" className="w-12 h-12 opacity-20" />
      <span className="text-[#d4af37]/40 text-3xl font-['Cormorant_Garamond'] font-medium tracking-wider">{initials}</span>
    </div>
  );
}

function ArtistImage({ artist, height }) {
  if (artist.image_url) {
    return <img src={artist.image_url} alt={artist.name} className={`w-full ${height} object-cover`} />;
  }
  return <ArtistPlaceholder name={artist.name} />;
}

function GenreTags({ genre }) {
  return (
    <div className="flex flex-wrap gap-2">
      {genre.split(', ').map(g => (
        <span key={g} className="text-xs px-3 py-1 bg-[#1c2a3d] border border-[#d4af37]/20 rounded-full text-[#a3b1c6]">{g}</span>
      ))}
    </div>
  );
}

function FeaturedArtistCard({ artist, isFirst }) {
  return (
    <div
      data-testid={`artist-card-${artist.id}`}
      className={`artist-card bg-[#122036] border border-[#d4af37]/20 rounded-xl overflow-hidden ${
        isFirst ? 'md:col-span-6 md:row-span-2' : 'md:col-span-6'
      }`}
    >
      <div className="relative h-48 md:h-56 overflow-hidden">
        <ArtistImage artist={artist} height="h-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#122036] via-transparent to-transparent" />
        <div className="absolute top-4 right-4">
          <span className="inline-block bg-[#1c2a3d] border border-[#d4af37]/30 text-[#d4af37] text-xs uppercase tracking-wider px-3 py-1 rounded-full">
            <Star className="w-3 h-3 inline mr-1" /> Featured
          </span>
        </div>
      </div>
      <div className="p-6 md:p-8">
        <p className="text-[#d4af37] text-xs uppercase tracking-wider mb-2">{artist.era}</p>
        <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#fdfbf7] mb-2">{artist.name}</h3>
        <p className="text-[#e5c35b] text-sm italic mb-3">{artist.title}</p>
        <p className="text-[#a3b1c6] text-sm leading-relaxed mb-4 line-clamp-3">{artist.bio}</p>
        <GenreTags genre={artist.genre} />
      </div>
    </div>
  );
}

function ArtistCard({ artist }) {
  return (
    <div
      data-testid={`artist-card-${artist.id}`}
      className="artist-card bg-[#122036] border border-[#d4af37]/20 rounded-xl overflow-hidden"
    >
      <div className="relative h-40 overflow-hidden">
        <ArtistImage artist={artist} height="h-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#122036] via-transparent to-transparent" />
      </div>
      <div className="p-6">
        <p className="text-[#d4af37] text-xs uppercase tracking-wider mb-1">{artist.era}</p>
        <h3 className="font-['Cormorant_Garamond'] text-xl text-[#fdfbf7] mb-1">{artist.name}</h3>
        <p className="text-[#e5c35b] text-xs italic mb-3">{artist.title}</p>
        <p className="text-[#a3b1c6] text-sm leading-relaxed line-clamp-2">{artist.bio}</p>
      </div>
    </div>
  );
}

export default function ArtistsSection() {
  const [artists, setArtists] = useState([]);

  const fetchArtists = useCallback(() => {
    axios.get(`${API}/artists`).then(r => setArtists(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  useEffect(() => { fetchArtists(); }, [fetchArtists]);

  const featured = useMemo(() => artists.filter(a => a.featured), [artists]);
  const others = useMemo(() => artists.filter(a => !a.featured), [artists]);

  return (
    <section id="artists" data-testid="artists-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.2em] text-[#d4af37] mb-4 flex items-center justify-center gap-2">
            <Music className="w-4 h-4" /> Maestros
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#fdfbf7] mb-4 tracking-tight">Notable Artists</h2>
          <p className="text-[#a3b1c6] max-w-xl mx-auto text-base">The luminaries who shaped the sitar's voice across generations</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-8">
          {featured.map((artist, i) => (
            <FeaturedArtistCard key={artist.id} artist={artist} isFirst={i === 0} />
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {others.map(artist => <ArtistCard key={artist.id} artist={artist} />)}
        </div>
      </div>
      <div className="section-divider mt-24 md:mt-32 max-w-4xl mx-auto" />
    </section>
  );
}
